import React from 'react';
import { CheckCircle, ExternalLink } from 'lucide-react';
import { Event } from '../../hooks/useEvents';
import { formatCurrency } from '../../lib/currencyUtils';
import { formatRegistrationDeadline } from '../../utils/dateUtils';

interface EventCardProps {
  event: Event;
  isRegistered: boolean;
  registrationCount: number;
  eventIsUpcoming: boolean;
  registrationOpen: boolean;
  isDriver: boolean;
  formatDateForDisplay: (date: string) => string;
  getVehicleIcon: (vehicleType: string) => JSX.Element;
  userRegistrations: any[];
  onClick: () => void;
}

const EventCard: React.FC<EventCardProps> = ({
  event,
  isRegistered,
  registrationCount,
  eventIsUpcoming,
  registrationOpen,
  isDriver,
  formatDateForDisplay,
  getVehicleIcon,
  userRegistrations,
  onClick
}) => {
  const handleCardClick = () => {
    // If there's a ticket shop URL and user is not a driver, open the URL
    if (event.ticket_shop_url && !isDriver) {
      window.open(event.ticket_shop_url, '_blank', 'noopener,noreferrer');
    } else {
      // Otherwise use the regular onClick handler
      onClick();
    }
  };

  return (
    <div
      className={`bg-zinc-900 rounded-lg overflow-hidden relative cursor-pointer hover:bg-zinc-800 transition-colors ${
        !eventIsUpcoming ? 'opacity-75' : ''
      }`}
      onClick={handleCardClick}
    >
      {/* Event Status Badges */}
      <div className="absolute top-3 right-3 z-10 flex flex-col gap-2">
        {/* Past Event Badge */}
        {!eventIsUpcoming && (
          <div className="bg-gray-600 text-white px-2 py-1 rounded-full text-xs font-semibold">
            Past Event
          </div>
        )}
        
        {/* Registration Status Badge */}
        {isDriver && isRegistered && (
          <div className="bg-green-600 text-white px-2 py-1 rounded-full text-xs font-semibold flex items-center gap-1">
            <CheckCircle className="h-3 w-3" />
            Registered ({registrationCount})
          </div>
        )}
      </div>

      {/* Event Flyer - Reduced height */}
      {event.track_image?.url && (
        <div className="aspect-[16/9] relative">
          <img
            src={event.track_image.url}
            alt={event.name}
            className="w-full h-full object-cover"
          />
          
          {/* Ticket Shop Button - More Prominent */}
          {event.ticket_shop_url && (
            <div className="absolute top-3 left-3 z-10">
              <div className="bg-primary-600 text-white px-3 py-1 rounded-full text-sm font-bold flex items-center gap-1">
                <ExternalLink className="h-3 w-3" />
                Buy Tickets
              </div>
            </div>
          )}
        </div>
      )}

      {/* Event Info - More compact */}
      <div className="p-4">
        <h2 className="text-xl font-bold text-white mb-2">{event.name}</h2>
        <p className="text-gray-400 text-sm mb-3 line-clamp-2">{event.description}</p>

        <div className="flex flex-col gap-2 text-xs text-gray-400 mb-3">
          <div className="flex items-center gap-2">
            <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-3 w-3">
              <rect width="18" height="18" x="3" y="4" rx="2" ry="2" />
              <line x1="16" x2="16" y1="2" y2="6" />
              <line x1="8" x2="8" y1="2" y2="6" />
              <line x1="3" x2="21" y1="10" y2="10" />
            </svg>
            <span>{formatDateForDisplay(event.event_date)}</span>
            {!eventIsUpcoming && (
              <span className="text-gray-500">(Past)</span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-3 w-3">
              <circle cx="12" cy="12" r="10" />
              <polyline points="12 6 12 12 16 14" />
            </svg>
            <span>Registration closes: {formatRegistrationDeadline(event.event_date)}</span>
          </div>
        </div>

        {/* User's Registrations for this Event - Compact */}
        {isDriver && userRegistrations.length > 0 && (
          <div className="mb-3 p-2 bg-green-900 border border-green-700 rounded">
            <h4 className="text-green-300 font-semibold mb-1 flex items-center gap-1 text-xs">
              <CheckCircle className="h-3 w-3" />
              Your Registrations:
            </h4>
            <div className="space-y-1">
              {userRegistrations.map((registration) => (
                <div key={registration.id} className="flex justify-between items-center text-xs">
                  <span className="text-green-200 flex items-center gap-1">
                    {getVehicleIcon(registration.category.vehicle_type)}
                    {registration.category.name}
                  </span>
                  <div className="flex items-center gap-1">
                    <span className="text-green-300">{formatCurrency(registration.category.registration_fee)}</span>
                    <span className={`px-1 py-0.5 rounded-full text-xs font-medium ${
                      registration.payment_status === 'paid' 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-yellow-100 text-yellow-800'
                    }`}>
                      {registration.payment_status === 'paid' ? 'Paid' : 'Pending'}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Categories Preview - More compact */}
        <div className="mb-3">
          <h3 className="text-sm font-semibold text-white mb-2">Categories Available</h3>
          <div className="flex flex-wrap gap-1">
            {event.event_categories.slice(0, 4).map((category) => (
              <span
                key={category.id}
                className={`px-2 py-1 text-xs rounded-full flex items-center gap-1 ${
                  category.vehicle_type === 'car'
                    ? 'bg-primary-100 text-primary-800'
                    : 'bg-primary-100 text-primary-800'
                }`}
              >
                {getVehicleIcon(category.vehicle_type)}
                {category.name}
              </span>
            ))}
            {event.event_categories.length > 4 && (
              <span className="px-2 py-1 text-xs bg-gray-100 text-gray-800 rounded-full">
                +{event.event_categories.length - 4} more
              </span>
            )}
          </div>
        </div>

        {/* Call to Action Text */}
        {isDriver ? (
          <div className="text-primary-500 text-xs font-medium">
            {isRegistered 
              ? `Click to manage your ${registrationCount} registration${registrationCount > 1 ? 's' : ''} →`
              : registrationOpen 
                ? 'Click to view categories and register →'
                : 'Click to view event details →'
            }
          </div>
        ) : event.ticket_shop_url ? (
          <div className="text-primary-500 text-sm font-medium flex items-center gap-1">
            <ExternalLink className="h-4 w-4" />
            Click to buy tickets
          </div>
        ) : (
          <div className="text-gray-400 text-xs">
            Click for event details
          </div>
        )}
      </div>
    </div>
  );
};

export default EventCard;